import { useRef, useState } from "react";
import { useT, type MessageKey } from "../../i18n/index.js";
import { compressImageToBase64 } from "../../lib/image-compress.js";
import { trpc } from "../../lib/trpc.js";

/**
 * F-tech-docs (CEO 2026-09-20) — banner laranja cobrando os documentos
 * legais. Decisão explícita do CEO: documento NÃO trava o início do trabalho;
 * o técnico começa e tem 7 dias para enviar. Por isso isto é um aviso com
 * upload embutido, nunca um bloqueio.
 */

const ROTULOS: Record<string, MessageKey> = {
  identity: "techPortal.docsIdentity2",
  urssaf: "techPortal.docsUrssaf",
  rc_pro: "techPortal.docsRcPro",
  kbis: "techPortal.docsKbis2",
  a1: "techPortal.docsA1",
};

export function TechnicianDocsBanner({ companyId }: { companyId: number }) {
  const t = useT();
  const utils = trpc.useUtils();
  const [enviando, setEnviando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const tipoRef = useRef<string>("identity");

  const docsQ = trpc.technicianPortal.myDocs.useQuery({ companyId });
  const upload = trpc.technicianPortal.uploadDoc.useMutation({
    onSuccess: () => {
      setEnviando(null);
      void utils.technicianPortal.myDocs.invalidate();
    },
    onError: (e) => {
      setEnviando(null);
      setErro(e.message);
    },
  });

  const docs = docsQ.data;
  if (!docs || docs.dueAt == null) return null;
  if (docs.missing.length === 0) return null;

  const dias = docs.daysLeft ?? 0;
  const atrasado = dias < 0;

  async function escolheu(file: File) {
    setErro(null);
    setEnviando(tipoRef.current);
    try {
      // Foto de documento vem do celular e pesa: comprime antes de subir.
      const base64 = file.type.startsWith("image/")
        ? await compressImageToBase64(file, 1600, 0.8)
        : await new Promise<string>((ok, falhou) => {
            const fr = new FileReader();
            fr.onload = () => ok(String(fr.result));
            fr.onerror = () => falhou(new Error("read"));
            fr.readAsDataURL(file);
          });
      upload.mutate({
        companyId,
        kind: tipoRef.current as "identity" | "urssaf" | "rc_pro" | "kbis" | "a1" | "other",
        filename: file.name,
        contentBase64: base64,
      });
    } catch {
      setEnviando(null);
      setErro("upload");
    }
  }

  return (
    <section
      className={`rounded-lg border px-3 py-2.5 ${
        atrasado ? "border-red-500/50 bg-red-500/10" : "border-amber-500/50 bg-amber-500/10"
      }`}
    >
      <p className={`text-sm font-semibold ${atrasado ? "text-red-300" : "text-amber-200"}`}>
        📄 {t("techPortal.docsBannerTitle")}
      </p>
      <p className="mt-0.5 text-xs text-neutral-300">
        {atrasado
          ? t("techPortal.docsOverdue")
          : dias === 0
            ? t("techPortal.docsLastDay")
            : `${t("techPortal.docsDaysLeftPre")} ${dias} ${t("techPortal.docsDaysLeftSuf")}`}
      </p>

      {erro && <p className="mt-1 text-xs text-red-300">{erro}</p>}

      <div className="mt-2 space-y-1.5">
        {docs.required.map((k) => {
          // URSSAF e A1 valem um pelo outro (CEO 2026-10-05): o item conta
          // como enviado se o servidor não o lista mais como faltante.
          const enviado = docs.sent.some((s) => s.kind === k) || !docs.missing.includes(k);
          return (
            <div key={k} className="flex items-center justify-between gap-2 text-xs">
              <span className={enviado ? "text-neutral-500 line-through" : "text-neutral-200"}>
                {t(ROTULOS[k] ?? "techPortal.docsIdentity2")}
              </span>
              {enviado ? (
                <span className="rounded bg-lime/15 px-1.5 py-0.5 text-[10px] text-lime">
                  ✓ {t("techPortal.docsSentBadge")}
                </span>
              ) : (
                <button
                  type="button"
                  disabled={enviando != null}
                  onClick={() => {
                    tipoRef.current = k;
                    inputRef.current?.click();
                  }}
                  className="rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-neutral-900 disabled:opacity-50"
                >
                  {enviando === k ? t("techPortal.docsSending") : t("techPortal.docsSendBtn")}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void escolheu(f);
        }}
      />
    </section>
  );
}
